/**
 * rsFavourites — Swym Wishlist Plus wrapper.
 *
 * Single source of truth for the brand favourites feature. Wraps Swym's
 * _swat global behind a stable API surface so consumers (cart-popup.js,
 * page-my-account.liquid, sections/header.liquid) never see Swym call
 * signatures or readiness ceremony directly.
 *
 * SDK readiness: pushes a callback into window.SwymCallbacks. The callback
 * runs once _swat is loaded, fetches the current wishlist, primes the
 * internal cache, then flushes any onReady() subscribers.
 *
 * Cache: an in-memory Map keyed by "empi:epi". list() always re-fetches
 * from Swym (truth source). has()/hasProduct()/count() read the cache
 * synchronously (so heart-button state can paint without awaiting).
 *
 * Events: subscribes to _swat.evtLayer for add/remove events. JSEvents
 * enum gives us the exact event-name constants — we read them at runtime
 * rather than hardcoding strings, so an SDK rename doesn't silently break us.
 *
 * Loaded BEFORE cart-popup.js in theme.liquid. The localStorage migration
 * block at the bottom of this file runs once on first SDK-ready, walking
 * any pre-Swym rs_favourites array and pushing entries into Swym via
 * addToList. Idempotent — subsequent loads see empty localStorage.
 */
(function () {
  'use strict';

  var _swat = null;
  var _ready = false;
  var _readyCbs = [];
  var _changeCbs = [];

  // cache: Map<"empi:epi", item> — primed by initial fetch, kept current
  // by post-add/post-remove updates from evtLayer events.
  var _cache = new Map();

  // Default list id, resolved from _swat.fetchLists() on first fetch and
  // memoised for subsequent calls. Required by Swym's fetchList({ lid })
  // (Phase 3 surfaced a 400 `{:lid missing-required-key}` because the
  // initial wrapper called fetchList without an lid).
  var _defaultLid = null;
  var _lidPromise = null;

  function _key(empi, epi) {
    return String(empi) + ':' + String(epi);
  }

  function _setCacheFromList(items) {
    _cache.clear();
    if (!Array.isArray(items)) return;
    items.forEach(function (item) {
      if (item && item.empi != null && item.epi != null) {
        _cache.set(_key(item.empi, item.epi), item);
      }
    });
  }

  // All cached rows whose empi matches (any variant). Product-level idiom,
  // same cache walk as hasProduct — returns the full row objects so callers
  // can act on each row's STORED epi/du (which may differ from a heart's
  // emitted variant id, and may be several rows if the pre-fix bug accreted
  // duplicates). Used by remove() to clear a product entirely.
  function _rowsForProduct(empi) {
    if (empi == null) return [];
    var target = String(empi);
    var rows = [];
    _cache.forEach(function (item) {
      if (item && String(item.empi) === target) rows.push(item);
    });
    return rows;
  }

  function _emitChange() {
    _changeCbs.forEach(function (cb) {
      try { cb(); } catch (e) { /* one bad subscriber shouldn't bail the rest */ }
    });
  }

  /**
   * Pick the preferred list from a Swym lists array. Heuristic:
   *   1. flagged `.isDefault`
   *   2. named "My Wishlist" or "Default Wishlist"
   *   3. first entry
   * SDK builds vary on `.lid` vs `.id` — callers should read both when
   * extracting the identifier.
   */
  function _pickPreferred(lists) {
    return lists.find(function (l) { return l && l.isDefault; }) ||
      lists.find(function (l) {
        if (!l || !l.lname) return false;
        var n = String(l.lname).toLowerCase();
        return n === 'my wishlist' || n === 'default wishlist';
      }) ||
      lists[0];
  }

  /**
   * Resolve the user's default list id. Memoised — only the first invocation
   * hits the network. Subsequent callers get the cached promise.
   *
   * Bootstrap path per Swym's canonical docs:
   *   1. `fetchLists` to enumerate existing lists.
   *   2. If non-empty, pick preferred and capture its lid.
   *   3. If empty, `createList({ lname: 'My Wishlist' })` to bootstrap one.
   *   4. On total failure (network, missing SDK methods, safety timeout),
   *      resolve null. Callers must handle null by rejecting cleanly —
   *      addToList/deleteFromList require a non-null lid as their first
   *      positional arg.
   *
   * Short-circuit: if `_defaultLid` was captured as a side effect of a
   * `_fetchFromSwym` call running first (bootstrap order), reuse it
   * instead of re-fetching.
   */
  function _ensureLid() {
    if (_lidPromise) return _lidPromise;
    if (_defaultLid) {
      _lidPromise = Promise.resolve(_defaultLid);
      return _lidPromise;
    }
    _lidPromise = new Promise(function (resolve) {
      if (!_swat || typeof _swat.fetchLists !== 'function') {
        resolve(null);
        return;
      }
      var safetyTimer = setTimeout(function () { resolve(null); }, 5000);
      _swat.fetchLists({
        callbackFn: function (lists) {
          clearTimeout(safetyTimer);
          if (Array.isArray(lists) && lists.length > 0) {
            var preferred = _pickPreferred(lists);
            _defaultLid = (preferred && (preferred.lid || preferred.id)) || null;
            resolve(_defaultLid);
            return;
          }
          // No lists yet — bootstrap one per Swym docs' canonical init flow.
          if (typeof _swat.createList !== 'function') {
            resolve(null);
            return;
          }
          try {
            _swat.createList(
              { lname: 'My Wishlist' },
              function (created) {
                _defaultLid = (created && (created.lid || created.id)) || null;
                resolve(_defaultLid);
              },
              function () { resolve(null); }
            );
          } catch (e) {
            resolve(null);
          }
        },
        errorFn: function () {
          clearTimeout(safetyTimer);
          resolve(null);
        }
      });
    });
    return _lidPromise;
  }

  /**
   * Fetch the user's wishlist contents from Swym.
   *
   * Per Swym docs, `fetchLists` returns every list the user owns, each with
   * its `listcontents` array embedded — a single round-trip gets both list
   * metadata AND items. We pick the preferred list (same heuristic as
   * `_ensureLid`) and return its `listcontents`.
   *
   * Side effect: if `_defaultLid` is still null (this call won the race
   * against `_ensureLid`), capture it from the preferred list. Avoids a
   * redundant `fetchLists` on bootstrap.
   *
   * Defence-in-depth: 5-second safety timer guarantees the outer promise
   * resolves even if Swym's callbacks never fire. Resolves with [] on any
   * failure path so consumers paint "no favourites" rather than hanging.
   */
  function _fetchFromSwym() {
    return new Promise(function (resolve) {
      if (!_swat || typeof _swat.fetchLists !== 'function') { resolve([]); return; }

      var settled = false;
      var settle = function (items) {
        if (settled) return;
        settled = true;
        resolve(items || []);
      };
      var safetyTimer = setTimeout(function () { settle([]); }, 5000);

      try {
        _swat.fetchLists({
          callbackFn: function (lists) {
            clearTimeout(safetyTimer);
            if (!Array.isArray(lists) || lists.length === 0) { settle([]); return; }
            var preferred = _pickPreferred(lists);
            if (preferred && !_defaultLid) {
              _defaultLid = (preferred.lid || preferred.id) || null;
            }
            settle(preferred && preferred.listcontents);
          },
          errorFn: function () {
            clearTimeout(safetyTimer);
            settle([]);
          }
        });
      } catch (e) {
        clearTimeout(safetyTimer);
        settle([]);
      }
    });
  }

  /**
   * Subscribe to Swym's add/remove events via evtLayer.
   *
   * Event-name constants come from _swat.JSEvents (we don't hardcode strings —
   * SDK build differences could rename them). If JSEvents lookup fails for a
   * given key, we skip subscribing for that event rather than throwing.
   *
   * On any event, re-fetch the list (Swym is source of truth) and emit
   * onChange. The post-event fetch is cheap because Swym caches client-side.
   */
  function _subscribeToSwymEvents() {
    if (!_swat || !_swat.evtLayer || !_swat.JSEvents) return;

    var refresh = function () {
      _fetchFromSwym().then(function (items) {
        _setCacheFromList(items);
        _emitChange();
      });
    };

    var addEvent    = _swat.JSEvents.addedToWishlist     || _swat.JSEvents.productAddedToWishlist;
    var removeEvent = _swat.JSEvents.removedFromWishlist || _swat.JSEvents.productRemovedFromWishlist;

    if (addEvent)    _swat.evtLayer.addEventListener(addEvent,    refresh);
    if (removeEvent) _swat.evtLayer.addEventListener(removeEvent, refresh);
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  /**
   * Qualify a URL with origin if relative. Defence-in-depth alongside the
   * cart-popup-side qualification: any caller (migration path, future
   * direct rsFavourites.add caller) that passes a relative du gets it
   * fixed up here. addToList/deleteFromList expect a fully-qualified `du`.
   */
  function _qualifyUrl(u) {
    if (!u || typeof u !== 'string') return u;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.indexOf('//') === 0) return window.location.protocol + u;
    if (u.indexOf('/') === 0)  return window.location.origin + u;
    return u;
  }

  /**
   * Build the minimal product object Swym's `addToList` / `deleteFromList`
   * accept: `{ empi, epi, du }`. Per docs (verified Phase 3 sixth-pass), the
   * canonical input contract for these methods is exactly these three
   * fields — variant ID, product ID, and a fully-qualified product URL.
   *
   * Display metadata (`iu`, `dt`, `pr`) is NOT part of the input contract;
   * Swym returns those fields on `listcontents[i]` and in evtLayer event
   * payloads via the platform's own catalog hydration.
   *
   * Defensive transforms:
   *   - empi/epi -> parseInt (callers may pass stringified IDs)
   *   - du       -> _qualifyUrl (Swym requires fully-qualified URLs)
   */
  function _swymProduct(productData) {
    return {
      empi: parseInt(productData.empi, 10),
      epi:  parseInt(productData.epi, 10),
      du:   _qualifyUrl(productData.du)
    };
  }

  window.rsFavourites = {
    /**
     * Add to wishlist. productData expected shape:
     *   { empi, epi, du, ...display metadata ignored by SDK }
     * The SDK only consumes empi/epi/du; cart-popup.js still passes the
     * fuller shape for historical reasons — harmless, dropped at the SDK
     * boundary by _swymProduct.
     */
    add: function (productData) {
      return new Promise(function (resolve, reject) {
        if (!_swat || typeof _swat.addToList !== 'function') {
          reject(new Error('rsFavourites.add: SDK not ready'));
          return;
        }
        if (productData == null || productData.empi == null || productData.epi == null) {
          reject(new Error('rsFavourites.add: missing empi/epi'));
          return;
        }
        var product = _swymProduct(productData);
        var key = _key(productData.empi, productData.epi);

        // 1. Optimistic cache update — heart fills immediately on click,
        // before the server round-trip completes.
        _cache.set(key, productData);
        _emitChange();

        _ensureLid().then(function (lid) {
          if (!lid) {
            // No list available and createList failed too. Roll back
            // optimistic state and reject — there's nothing to fetch from.
            _cache.delete(key);
            _emitChange();
            reject(new Error('rsFavourites.add: no wishlist available'));
            return;
          }

          // 2-4. Server-truth refresh decides resolve/reject. Fifth-pass
          // belt-and-braces retained while we verify the canonical
          // addToList surface fires callbacks cleanly. If post-deploy
          // verification shows onSuccess/onError reliable, the refresh can
          // move to the error path only (follow-up commit).
          var refreshed = false;
          var refresh = function (advisoryErr) {
            if (refreshed) return;
            refreshed = true;
            if (advisoryErr) {
              console.debug('rsFavourites.add: SDK onError (advisory)', advisoryErr);
            }
            _fetchFromSwym().then(function (items) {
              // _setCacheFromList overwrites with server truth — auto-rollback
              // if the optimistic add didn't persist.
              _setCacheFromList(items);
              _emitChange();
              if (_cache.has(key)) {
                resolve(items);
              } else {
                reject(new Error('rsFavourites.add: server did not confirm persist'));
              }
            });
          };

          // 5. Safety timeout — refresh runs even if neither SDK callback
          // fires. Single dedup flag ensures at most one refresh per call.
          var safetyTimer = setTimeout(function () { refresh(); }, 5000);

          try {
            _swat.addToList(
              lid,
              product,
              function (/* addedListItem */) {
                clearTimeout(safetyTimer);
                refresh();
              },
              function (err) {
                clearTimeout(safetyTimer);
                refresh(err);
              }
            );
          } catch (e) {
            // SDK threw synchronously. Kick refresh immediately.
            clearTimeout(safetyTimer);
            refresh(e);
          }
        });
      });
    },

    /**
     * Remove a product from the wishlist — PRODUCT-LEVEL (Option A). Membership
     * and hearts are keyed by product (empi), so removal must clear EVERY row
     * for that empi, not just the one variant the caller happens to hold. A
     * product may have several rows: a legitimately-different colourway, OR
     * duplicates the pre-fix variant-mismatch bug accreted. Leaving any row
     * behind keeps hasProduct() true and the heart painted filled — the
     * un-favourite would look broken.
     *
     * Takes the same productData shape as add(); only empi is required. Each
     * matching row is deleted via Swym's deleteFromList using the row's OWN
     * stored epi/du (the identity Swym persisted), not the caller's epi. If the
     * cache has no row yet (remove requested before the first fetch primed it),
     * fall back to the caller's payload so a lone thin call still works.
     */
    remove: function (productData) {
      return new Promise(function (resolve, reject) {
        if (!_swat || typeof _swat.deleteFromList !== 'function') {
          reject(new Error('rsFavourites.remove: SDK not ready'));
          return;
        }
        if (productData == null || productData.empi == null) {
          reject(new Error('rsFavourites.remove: missing empi'));
          return;
        }

        // Gather every cached row for this product. Fall back to the caller's
        // own payload when the cache is cold so a single delete still fires.
        var rows = _rowsForProduct(productData.empi);
        if (rows.length === 0) rows = [productData];

        // Build one delete target per row. Swym's deleteFromList needs a du to
        // identify the entry; thin callers (drawer Remove) pass du:'' — fall
        // back to the row's stored du (or, last resort, the caller's) so the
        // SDK call resolves instead of silently no-oping server-side.
        var targets = rows.map(function (row) {
          var key = _key(row.empi, row.epi);
          var du  = row.du || productData.du || '';
          return {
            key: key,
            previous: _cache.get(key) || row,
            product: {
              empi: parseInt(row.empi, 10),
              epi:  parseInt(row.epi, 10),
              du:   _qualifyUrl(du)
            }
          };
        });

        // 1. Optimistic cache delete of every matching row — heart empties
        // immediately, before the server round-trip.
        targets.forEach(function (t) { _cache.delete(t.key); });
        _emitChange();

        _ensureLid().then(function (lid) {
          if (!lid) {
            // No list available — restore optimistic state for every row.
            targets.forEach(function (t) {
              if (t.previous) _cache.set(t.key, t.previous);
            });
            _emitChange();
            reject(new Error('rsFavourites.remove: no wishlist available'));
            return;
          }

          // Fire deleteFromList for every target; a single server-truth refresh
          // runs once all deletes settle (or the safety timer fires) and decides
          // resolve/reject. Success is PRODUCT-LEVEL: zero rows left for the
          // empi, not just the row we started from.
          var refreshed = false;
          var refresh = function (advisoryErr) {
            if (refreshed) return;
            refreshed = true;
            clearTimeout(safetyTimer);
            if (advisoryErr) {
              console.debug('rsFavourites.remove: SDK onError (advisory)', advisoryErr);
            }
            _fetchFromSwym().then(function (items) {
              _setCacheFromList(items);
              _emitChange();
              if (_rowsForProduct(productData.empi).length === 0) {
                resolve(items);
              } else {
                reject(new Error('rsFavourites.remove: server still has item(s)'));
              }
            });
          };

          // Barrier: refresh only after the last delete callback returns.
          var pending = targets.length;
          var settleOne = function (err) {
            if (err) {
              console.debug('rsFavourites.remove: deleteFromList onError (advisory)', err);
            }
            pending -= 1;
            if (pending <= 0) refresh();
          };

          var safetyTimer = setTimeout(function () { refresh(); }, 5000);

          targets.forEach(function (t) {
            try {
              _swat.deleteFromList(
                lid,
                t.product,
                function (/* deletedProduct */) { settleOne(); },
                function (err) { settleOne(err); }
              );
            } catch (e) {
              settleOne(e);
            }
          });
        });
      });
    },

    /**
     * Fetch fresh wishlist contents from Swym. Updates cache as a side effect
     * so subsequent has()/count() reads are accurate.
     */
    list: function () {
      return _fetchFromSwym().then(function (items) {
        _setCacheFromList(items);
        return items;
      });
    },

    /** Sync after first .list() / SDK ready. Variant-scoped. */
    has: function (empi, epi) {
      if (empi == null || epi == null) return false;
      return _cache.has(_key(empi, epi));
    },

    /** Sync after first .list() / SDK ready. Product-level: any variant counts. */
    hasProduct: function (empi) {
      if (empi == null) return false;
      var target = String(empi);
      var found = false;
      _cache.forEach(function (item) {
        if (!found && item && String(item.empi) === target) found = true;
      });
      return found;
    },

    /**
     * All cached rows for a product (any variant). Product-level sibling of
     * hasProduct — returns the stored row objects (each with its own epi/du)
     * so callers can act on what's actually persisted, not on a variant id
     * they happen to hold. Empty array when the product isn't saved.
     */
    rowsForProduct: function (empi) {
      return _rowsForProduct(empi);
    },

    /** Sync after first .list() / SDK ready. */
    count: function () {
      return _cache.size;
    },

    /** Fires once SDK is ready and first fetch has completed. */
    onReady: function (cb) {
      if (typeof cb !== 'function') return;
      if (_ready) { try { cb(); } catch (e) {} return; }
      _readyCbs.push(cb);
    },

    /** Fires after every add/remove. */
    onChange: function (cb) {
      if (typeof cb !== 'function') return;
      _changeCbs.push(cb);
    },

    /** Read-only flag for consumers that need to gate work on readiness. */
    get isReady() { return _ready; }
  };

  // ─── One-time localStorage -> Swym migration ────────────────────────────
  //
  // Pre-Swym storage was localStorage['rs_favourites'], an array of
  // { handle, title, image, price, url }. After initial Swym fetch we walk
  // any remaining localStorage entries: derive empi (product.id) + epi
  // (first variant id) via /products/{handle}.js and push to Swym via
  // addToList. Idempotent — subsequent loads see an empty localStorage and
  // skip. On partial failure, only the failed items are persisted back for
  // next-session retry; everything else (succeeded, already-in-Swym, or
  // product-deleted) is dropped from localStorage.
  function _migrateLocalStorageIfNeeded() {
    var raw;
    try { raw = localStorage.getItem('rs_favourites'); } catch (e) { return; }
    if (!raw) return;

    var stored;
    try { stored = JSON.parse(raw); } catch (e) {
      try { localStorage.removeItem('rs_favourites'); } catch (e2) {}
      return;
    }
    if (!Array.isArray(stored)) {
      try { localStorage.removeItem('rs_favourites'); } catch (e) {}
      return;
    }
    var pending = stored.filter(function (x) { return x && typeof x === 'object' && x.handle; });
    if (pending.length === 0) {
      try { localStorage.removeItem('rs_favourites'); } catch (e) {}
      return;
    }

    // Resolve lid once for the whole batch. If unavailable, bail without
    // touching localStorage — next session retries.
    _ensureLid().then(function (lid) {
      if (!lid) return;

      // Per-item callback err is advisory only. Each attempt fires-and-waits
      // for either the SDK callback or a 5s safety timer; a single batched
      // _fetchFromSwym() afterwards decides which items actually persisted
      // (server-truth). Items not in the refreshed list are kept in
      // localStorage for next-session retry.
      Promise.all(pending.map(function (item) {
        return fetch('/products/' + item.handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (product) {
            if (!product || !product.variants || !product.variants[0]) {
              // Product 404'd or has no variants — drop it. Trying again
              // next session will produce the same outcome.
              return { item: item, attempted: false };
            }
            var firstVariant = product.variants[0];
            var swymData = {
              empi: product.id,
              epi:  firstVariant.id,
              du:   window.location.origin + (item.url || '/products/' + item.handle)
            };
            if (_cache.has(_key(swymData.empi, swymData.epi))) {
              return { item: item, attempted: true, alreadyIn: true, swymData: swymData };
            }
            return new Promise(function (resolve) {
              var settled = false;
              var settle = function () {
                if (settled) return;
                settled = true;
                resolve({ item: item, attempted: true, alreadyIn: false, swymData: swymData });
              };
              var safety = setTimeout(settle, 5000);
              try {
                _swat.addToList(
                  lid,
                  _swymProduct(swymData),
                  function () { clearTimeout(safety); settle(); },
                  function (err) {
                    clearTimeout(safety);
                    console.debug('rsFavourites migration: SDK onError (advisory)', err);
                    settle();
                  }
                );
              } catch (e) {
                clearTimeout(safety);
                console.debug('rsFavourites migration: addToList threw', e);
                settle();
              }
            });
          });
      })).then(function (attempts) {
        // One batched refresh decides per-item outcomes by server truth.
        return _fetchFromSwym().then(function (items) {
          _setCacheFromList(items);
          _emitChange();

          var addedCount = 0;
          var retryItems = [];
          attempts.forEach(function (a) {
            if (!a.attempted) return;       // product 404 / no variants — drop
            if (a.alreadyIn) return;        // already in Swym pre-attempt — drop
            var k = _key(a.swymData.empi, a.swymData.epi);
            if (_cache.has(k)) addedCount++;
            else retryItems.push(a.item);
          });

          if (retryItems.length === 0) {
            try { localStorage.removeItem('rs_favourites'); } catch (e) {}
          } else {
            try { localStorage.setItem('rs_favourites', JSON.stringify(retryItems)); } catch (e) {}
            console.warn('[rs-favourites] ' + retryItems.length +
                         ' items failed to migrate to Swym; will retry next session');
          }
          if (addedCount > 0) {
            console.log('[rs-favourites] migrated ' + addedCount +
                        ' items from localStorage to Swym');
          }
        });
      });
    });
  }

  // ─── SDK bootstrap ──────────────────────────────────────────────────────

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    _swat = swat;
    _subscribeToSwymEvents();
    _fetchFromSwym().then(function (items) {
      _setCacheFromList(items);
      _ready = true;
      _readyCbs.forEach(function (cb) {
        try { cb(); } catch (e) {}
      });
      _readyCbs.length = 0;
      _emitChange(); // initial paint trigger for any onChange subscribers already registered
      _migrateLocalStorageIfNeeded();
    });
  });
})();
